// Pure jitter + rev-watch logic for live contact auto-refresh. Kept DOM/timer-free
// so it's unit-testable in isolation. See docs/CONTACT-SYNC.md ("live field-update
// fanout") and portal/src/views/contacts.ts for wiring.
//
// When an owner changes a field, the backend fans out a per-subscriber "contacts
// revision" bump plus an active_peers hint (how many subscribers were notified in
// the same fanout). Each subscriber's contacts view polls /contacts/refresh-signal;
// on a rev CHANGE it schedules a JITTERED /contacts refetch, spread over a window
// proportional to active_peers so N subscribers don't stampede the backend at once.

// Floor so even a single peer jitters a little (avoids a same-tick refetch).
export const JITTER_FLOOR_MS = 150;
// Per-peer scaling and the absolute ceiling on the jitter window.
export const JITTER_PER_PEER_MS = 300;
export const JITTER_MAX_MS = 15000;

// jitterDelay returns a random delay (ms) in [0, window] where the window grows
// with activePeers: window = clamp(JITTER_PER_PEER_MS * peers, JITTER_FLOOR_MS,
// JITTER_MAX_MS). `rand` is injectable for deterministic tests (defaults to
// Math.random). Non-finite / negative peer counts are treated as a single peer.
export function jitterWindow(activePeers: number): number {
  const peers = Number.isFinite(activePeers) && activePeers > 0 ? Math.floor(activePeers) : 1;
  const raw = JITTER_PER_PEER_MS * peers;
  return Math.min(JITTER_MAX_MS, Math.max(JITTER_FLOOR_MS, raw));
}

export function jitterDelay(activePeers: number, rand: () => number = Math.random): number {
  const window = jitterWindow(activePeers);
  const r = Math.min(1, Math.max(0, rand()));
  return Math.round(r * window);
}

// RevWatcher tracks the last-seen contacts revision and decides, on each polled
// signal, whether a jittered refresh should be scheduled. It COALESCES: while a
// refresh is already pending, further rev changes only update the peer hint (no
// stacked timers). Purely a state machine — the caller owns the actual timer.
export interface RevSignal {
  rev: string | number;
  active_peers: number;
}

export type RevDecision =
  | { action: 'skip' }
  | { action: 'schedule'; delayMs: number; activePeers: number };

export class RevWatcher {
  private lastRev: string | null = null;
  private pending = false;
  private rand: () => number;

  constructor(rand: () => number = Math.random) {
    this.rand = rand;
  }

  // Prime the watcher with the initial rev WITHOUT scheduling (first observation
  // is a baseline, not a change). Call once before polling begins.
  prime(rev: string | number): void {
    this.lastRev = String(rev);
  }

  // observe processes a polled signal. Returns {schedule, delay} when a NEW rev
  // warrants a (jittered) refresh and none is already pending; otherwise {skip}.
  // A rev change while a refresh is pending is coalesced into the existing one.
  observe(sig: RevSignal): RevDecision {
    const rev = String(sig.rev);
    if (this.lastRev === null) {
      // Not primed — treat as baseline.
      this.lastRev = rev;
      return { action: 'skip' };
    }
    if (rev === this.lastRev) return { action: 'skip' };
    this.lastRev = rev;
    if (this.pending) return { action: 'skip' }; // coalesce into the in-flight refresh
    this.pending = true;
    return {
      action: 'schedule',
      delayMs: jitterDelay(sig.active_peers, this.rand),
      activePeers: sig.active_peers,
    };
  }

  // settle clears the pending flag once a scheduled refresh has run, so future
  // rev changes can schedule again.
  settle(): void {
    this.pending = false;
  }

  isPending(): boolean {
    return this.pending;
  }
}
